import {
  Button,
  Datagrid,
  DatagridCell,
  DatagridColumn,
  DatagridColumns,
  DatagridFilter,
  DatagridFooter,
  DatagridPagination,
  DatagridPlaceholder,
  DatagridRow,
  DatagridRows,
  FilterOption,
  FilterStateChange,
  MultiSelectFilter,
  MultiSelectFilterType,
  PaginationStateChange
} from '@vmware-react/clarity';
import React, { FC, useEffect, useState } from 'react';
import {
  ValidationGroup,
  ValidationItem,
  ValidationStatus,
  ValidationStatusLabels
} from 'vrlcm-gui-types';
import { Collapsible } from '../collapsible';
import { HRow } from '../h-row';
import { formatNumber, l10n } from '../with-app-root/i18n/i18nUtil';
import { getStatusCount, getStatusIcon, getStatusLabel } from './element-utils';

interface Props {
  group: ValidationGroup;
  onClickRecommendations: (recommendations: string[]) => void;
  onConsentChange: (group: string, consent: boolean) => void;
  initialCollapsed?: boolean;
}

interface Consent {
  [name: string]: boolean;
}

const filterByStatusType = (allPossibleStatus: ValidationStatus[]) => {
  const options: FilterOption[] = allPossibleStatus
    .map(so => [so, ValidationStatusLabels[so]])
    .map(([id, displayValue]) => {
      return {
        id,
        displayValue,
        checked: true
      };
    });
  options.splice(0, 0, {
    id: 'all',
    displayValue: l10n('components.preCheck.allTypes'),
    selectAllType: true,
    checked: true
  });
  return (
    <DatagridFilter>
      <MultiSelectFilter options={options} maxHeight="320px" />
    </DatagridFilter>
  );
};

const PrecheckValidationGroup: FC<Props> = props => {
  const {
    group,
    onClickRecommendations,
    onConsentChange,
    initialCollapsed
  } = props;
  const { items, name } = group;
  const initialConsent = items
    .filter(i => i.status === 'USER_WARNING')
    .reduce<Consent>((acc, i) => {
      return {
        ...acc,
        [i.name]: false
      };
    }, {});
  const [consent, setConsent] = useState(initialConsent);
  const onItemConsentChange = (item: ValidationItem) => () => {
    setConsent(p => ({ ...p, [item.name]: true }));
  };
  useEffect(() => {
    const consentValues = Object.values(consent);
    if (consentValues.length > 0) {
      const c = consentValues.every(i => i);
      onConsentChange(name, c);
    } else {
      onConsentChange(name, true);
    }
  }, [name, consent, onConsentChange]);
  const [collapsed, setColapsed] = useState(
    initialCollapsed !== undefined ? initialCollapsed : true
  );
  const onToggleCb = () => setColapsed(!collapsed);
  const [pageState, setPageState] = useState({
    currentPage: 1,
    pageSize: 5
  });
  const [statusFilter, setStatusFilter] = useState<MultiSelectFilterType>();
  const filteredItems = statusFilter
    ? statusFilter.some(sf => !!(sf.selectAllType && sf.checked))
      ? items
      : items.filter(it =>
          statusFilter.filter(sf => sf.checked).some(sf => sf.id === it.status)
        )
    : items;
  const totalItems = filteredItems.length;
  const { currentPage, pageSize } = pageState;
  const fromIndex = (currentPage - 1) * pageSize;
  const toIndex = Math.min(fromIndex + pageSize, totalItems);
  const visibleItems = filteredItems.slice(fromIndex, toIndex);

  const title = (
    <span className="validation-group-header">
      <span>{name}</span>
      <span className="validation-group-header-labels">
        {getStatusLabel(items)}
      </span>
    </span>
  );

  const onStateChange = (
    pagination: PaginationStateChange,
    filter: FilterStateChange
  ) => {
    if (pagination.changed) {
      setPageState(pagination.data);
    }
    if (filter.changed) {
      const statusFilterDg = filter.data.find(
        f => f.colIndex === 0 && f.applied
      );
      setStatusFilter(
        statusFilterDg
          ? (statusFilterDg.value as MultiSelectFilterType)
          : undefined
      );
    }
  };

  const datagridColumns = [
    <DatagridColumn
      key="status"
      filter={filterByStatusType(
        Object.keys(getStatusCount(items)) as ValidationStatus[]
      )}
    >
      {l10n('status')}
    </DatagridColumn>,
    <DatagridColumn key="check">{l10n('check')}</DatagridColumn>,
    <DatagridColumn key="component">
      {l10n('components.preCheck.componentOrResource')}
    </DatagridColumn>,
    <DatagridColumn key="result">
      {l10n('components.preCheck.resultDescription')}
    </DatagridColumn>,
    <DatagridColumn key="recommendations">
      {l10n('recommendations')}
    </DatagridColumn>
  ];

  const hasConsent = visibleItems.some(i => i.status === 'USER_WARNING');
  if (hasConsent) {
    datagridColumns.splice(
      4,
      0,
      <DatagridColumn key="consent">{l10n('consent')}</DatagridColumn>
    );
  }

  const renderDatagridCells = (item: ValidationItem) => {
    const onClickRecommendationsCb = Array.isArray(item.recommendations)
      ? () => onClickRecommendations(item.recommendations as string[])
      : null;
    const cells = [
      <DatagridCell key="status">
        <HRow>
          {getStatusIcon(item.status)}
          <span>{ValidationStatusLabels[item.status]}</span>
        </HRow>
      </DatagridCell>,
      <DatagridCell key="check">{item.name}</DatagridCell>,
      <DatagridCell key="component">{item.component}</DatagridCell>,
      <DatagridCell key="result">{item.result}</DatagridCell>,
      <DatagridCell key="recommendations">
        {onClickRecommendationsCb && (
          <Button
            type="button"
            family="link"
            size="sm"
            icon={true}
            onClick={onClickRecommendationsCb}
          >
            {l10n('view')}
          </Button>
        )}
      </DatagridCell>
    ];
    if (hasConsent) {
      cells.splice(
        4,
        0,
        <DatagridCell key="consent">
          {item.status === 'USER_WARNING' && (
            <Button
              size="sm"
              family="outline"
              disabled={consent[item.name]}
              onClick={onItemConsentChange(item)}
            >
              {consent[item.name] ? l10n('accepted') : l10n('accept')}
            </Button>
          )}
        </DatagridCell>
      );
    }
    return cells;
  };

  const renderDatagridColumns = () => {
    return <DatagridColumns>{datagridColumns}</DatagridColumns>;
  };

  const renderDatagridRows = () => {
    return (
      <DatagridRows>
        {visibleItems.map((item, index) => {
          return (
            <DatagridRow key={index} rowKey={item.name}>
              {renderDatagridCells(item)}
            </DatagridRow>
          );
        })}
      </DatagridRows>
    );
  };

  const renderDatagridPlaceholder = () => {
    return <DatagridPlaceholder>{l10n('noData')}</DatagridPlaceholder>;
  };

  const renderDatagridPagination = () => {
    return <DatagridPagination pageSize={pageSize} totalRows={totalItems} />;
  };

  const renderDatagridFooter = () => {
    return (
      <DatagridFooter pagination={renderDatagridPagination()}>
        {totalItems ? (
          <span>
            {l10n('components.preCheck.pagination', [
              formatNumber(fromIndex + 1),
              formatNumber(toIndex),
              formatNumber(totalItems)
            ])}
          </span>
        ) : (
          l10n('noResults')
        )}
      </DatagridFooter>
    );
  };

  return (
    <Collapsible title={title} collapsed={collapsed} onToggle={onToggleCb}>
      <div className="validation-group-container">
        <Datagrid
          onStateChange={onStateChange}
          columns={renderDatagridColumns()}
          placeholder={renderDatagridPlaceholder()}
          footer={renderDatagridFooter()}
          rows={renderDatagridRows()}
        />
      </div>
    </Collapsible>
  );
};

export default PrecheckValidationGroup;
